import { useCallback, useLayoutEffect } from 'react';
import { matchPath } from 'react-router';
import { identity, isEmpty, isEqual } from 'lodash';
import { useDrawerStack } from '../../../../hooks/queryParams/useDrawerStack';
import { useMultiLayerDrawerEnabled } from '../../../multi-layer-drawer/hooks/useMultiLayerDrawerEnabled';
import { useIsLastLayer } from '../../formData/hooks/useIsLastLayer';
import { useNavigationBlocker } from './useNavigationBlocker';

export const useUnsavedChangesController = ({
    formPath = '',
    hasDataChanges,
    onBeforeConfirmCancelNavigation = identity,
}) => {
    const isMultiLayerDrawerEnabled = useMultiLayerDrawerEnabled();
    const { stack } = useDrawerStack();
    const isLastLayer = useIsLastLayer(formPath);

    const skipBlockNavigationCondition = useCallback(
        (pathname, search) => {
            if (isMultiLayerDrawerEnabled) {
                try {
                    const params = new URLSearchParams(search);
                    const newStack = JSON.parse(params.get('drawerStack') || '[]');

                    if (newStack.length > stack.length) {
                        return isEqual(stack, newStack.slice(0, newStack.length - 1));
                    }

                    if (newStack.length === stack.length) {
                        return isEqual(stack, newStack);
                    }

                    return false;
                } catch {
                    return false;
                }
            }

            return matchPath(pathname, { path: formPath }) !== null;
        },
        [formPath, isMultiLayerDrawerEnabled, stack]
    );

    const blockNavigation = useNavigationBlocker({ skipBlockNavigationCondition, onBeforeConfirmCancelNavigation });

    // In this effect we activate unsaved changes tracker to prevent user leaving the page with the confirm modal
    useLayoutEffect(() => {
        if (!hasDataChanges || (isMultiLayerDrawerEnabled && !isLastLayer) || isEmpty(formPath)) {
            return;
        }

        const unblockNavigation = blockNavigation();

        return unblockNavigation;
    }, [blockNavigation, isLastLayer, isMultiLayerDrawerEnabled, formPath, hasDataChanges]);
};
